import axios, { AxiosResponse } from 'axios';
import apiClient from './client';

export interface Course {
  id: string;
  title: string;
  description: string;
  coverImage?: string;
  thumbnail_url?: string;
  lessons?: Lesson[];
  createdAt: string;
  updatedAt: string;
  is_enrolled?: boolean;
  is_published?: boolean;
  lesson_count?: number;
}

export interface Lesson {
  id: string;
  title: string;
  content: string;
  order_index?: number;
  order?: number;
  course_id?: string;
  courseId?: string;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  video_url?: string;
  thumbnail_url?: string;
  is_published?: boolean;
}

export interface CourseCreateData {
  title: string;
  description: string;
  coverImage?: string;
}

export interface LessonCreateData {
  title: string;
  content: string;
  order: number;
  courseId: string;
  video_url?: string;
  thumbnail_url?: string;
  is_published?: boolean;
}

export interface CourseEnrollment {
  user_id: number;
  course_id: string;
  enrolled_at: string;
  enrollment_status: string;
  last_accessed: string;
  created_at: string;
  updated_at: string;
  title?: string;
  description?: string;
  thumbnail_url?: string;
  app_id?: string | number;
}

// Get the app ID from environment
const APP_ID = process.env.REACT_APP_APP_ID || '1';

export const courseService = {
  // Course operations
  getCourses: (params?: any) => 
    apiClient.get<{success: boolean, data: Course[]}>(`/apps/${APP_ID}/courses`, { params })
      .then(response => {
        console.log('getCourses response:', response);
        return response;
      })
      .catch(error => {
        console.error('getCourses error:', error);
        throw error;
      }),
  
  // Get available courses (with enrollment status)
  getAvailableCourses: () =>
    apiClient.get<{success: boolean, data: Course[]}>(`/apps/${APP_ID}/available-courses`)
      .then(response => {
        console.log('getAvailableCourses response:', response);
        return response;
      })
      .catch(error => {
        console.error('getAvailableCourses error:', error);
        throw error;
      }),
  
  // Get enrolled courses
  getEnrolledCourses: () =>
    apiClient.get<{success: boolean, data: CourseEnrollment[]}>(`/enrollments`)
      .then(response => {
        console.log('getEnrolledCourses response:', response);
        return response;
      })
      .catch(error => {
        console.error('getEnrolledCourses error:', error);
        throw error;
      }),
  
  // Get course enrollments (admin only)
  getCourseEnrollments: (courseId: string) =>
    apiClient.get<{success: boolean, data: any[]}>(`/courses/${courseId}/enrollments`),
  
  getCourseById: (id: string) => 
    apiClient.get<{success: boolean, data: Course}>(`/courses/${id}`)
      .then(response => {
        console.log('getCourseById response:', response);
        return response;
      })
      .catch(error => {
        console.error('getCourseById error:', error);
        throw error;
      }),
  
  createCourse: (courseData: CourseCreateData) => 
    apiClient.post<{success: boolean, data: Course}>(`/apps/${APP_ID}/courses`, courseData),
  
  updateCourse: (id: string, courseData: Partial<CourseCreateData>) => 
    apiClient.put<{success: boolean, data: Course}>(`/courses/${id}`, courseData),
  
  deleteCourse: (id: string) => 
    apiClient.delete(`/courses/${id}`),
  
  // Lesson operations
  getLessons: (courseId: string) => 
    apiClient.get<{success: boolean, data: Lesson[]}>(`/courses/${courseId}/lessons`),
  
  getLessonById: (courseId: string, lessonId: string) => 
    apiClient.get<{success: boolean, data: Lesson}>(`/courses/${courseId}/lessons/${lessonId}`),
  
  createLesson: (lessonData: LessonCreateData) => 
    apiClient.post<{success: boolean, data: Lesson}>(`/courses/${lessonData.courseId}/lessons`, lessonData),
  
  updateLesson: (courseId: string, lessonId: string, lessonData: Partial<LessonCreateData>) => 
    apiClient.put<{success: boolean, data: Lesson}>(`/lessons/${lessonId}`, lessonData),
  
  deleteLesson: (courseId: string, lessonId: string) => 
    apiClient.delete(`/courses/${courseId}/lessons/${lessonId}`),
  
  // Enrollment operations
  enrollInCourse: (courseId: string) => 
    apiClient.post<{success: boolean, message: string, data: CourseEnrollment}>(`/courses/${courseId}/enroll`)
      .then(response => {
        console.log('enrollInCourse response:', response);
        return response;
      })
      .catch(error => {
        console.error('enrollInCourse error:', error);
        throw error;
      }),
  
  unenrollFromCourse: (courseId: string) =>
    apiClient.post<{success: boolean, message: string}>(`/courses/${courseId}/unenroll`)
      .then(response => {
        console.log('unenrollFromCourse response:', response);
        return response;
      })
      .catch(error => {
        console.error('unenrollFromCourse error:', error);
        throw error;
      }),
  
  // User progress
  getCourseProgress: (courseId?: string): Promise<AxiosResponse<{success: boolean, data: any}>> => {
    if (!courseId) {
      return apiClient.get<{success: boolean, data: any}>(`/courses/progress`);
    }
    
    return apiClient.get<{success: boolean, data: any}>(`/courses/${courseId}/progress`);
  },
  
  updateLessonProgress: (courseId: string, lessonId: string, completed: boolean) => 
    apiClient.post(`/lessons/${lessonId}/progress`, { is_completed: completed }),
  
  // Video uploads
  uploadLessonVideo: (lessonId: string, videoFile: File, progressCallback?: (progress: number) => void) => {
    // Server may have stricter limits than documented
    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB to match server configuration
    
    // Validate file size
    if (videoFile.size > MAX_FILE_SIZE) {
      return Promise.reject(new Error(
        `File size exceeds limit. Maximum allowed size is ${MAX_FILE_SIZE / (1024 * 1024)}MB. Please compress your video before uploading.`
      ));
    }
    
    // Validate file format
    const supportedFormats = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 
                             'video/x-ms-wmv', 'video/x-flv', 'video/x-matroska'];
    const fileType = videoFile.type;
    
    if (!supportedFormats.includes(fileType) && 
        !supportedFormats.some(format => videoFile.name.toLowerCase().endsWith(format.split('/')[1]))) {
      return Promise.reject(new Error(
        'Unsupported video format. Supported formats: MP4, WebM, MOV, AVI, WMV, FLV, MKV'
      ));
    }
    
    const formData = new FormData();
    formData.append('video', videoFile, videoFile.name);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(new Error('Upload timed out. Please try again with a smaller file.'));
      }, 30 * 60 * 1000); // 30 minute timeout
      
      // Set up progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && progressCallback) {
          const progress = Math.round((event.loaded / event.total) * 100);
          progressCallback(progress);
        }
      });
      
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          clearTimeout(timeoutId);
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success) {
                resolve(response.data);
              } else {
                reject(new Error(response.error || 'Failed to upload video'));
              }
            } catch (error) {
              reject(new Error('Failed to parse server response'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || 'Failed to upload video'));
            } catch (error) {
              reject(new Error('Failed to upload video'));
            }
          }
        }
      };
      
      xhr.open('POST', `${process.env.REACT_APP_API_URL || 'https://backend.saltmarshdigital.com/api'}/lessons/${lessonId}/upload-video`);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
      xhr.setRequestHeader('x-app-id', process.env.REACT_APP_APP_ID || '1');
      xhr.send(formData);
    });
  }
}; 